export {
  swrConfig,
  swrKeys,
  CACHE_TIMES,
  // Admin context helpers
  ADMIN_BUSINESS_CONTEXT_HEADER,
  getAdminSelectedBusinessId,
  getAdminContextHeaders,
  fetchWithAdminContext,
} from './config';
export {
  // Business hooks
  useBusiness,
  useBusinessHours,
  useBusinessSettings,
  invalidateBusinessSettings,

  // Services and staff hooks
  useServices,
  useAllServices,
  useStaffMembers,
  useAllStaffMembers,

  // Booking hooks
  useBookingsForDate,
  useBookingsForDateRange,
  useUpcomingBookings,
  useDueBookings,
  useBarberAvailability,
  useStaffHours,

  // Other data hooks
  useAlerts,
  useTodayEarnings,
  useTodayEarningsForStaff,
  useCustomers,
  useCustomerSearch,
  useCustomerOffers,

  // Inbox hooks
  useInboxMessages,
  useUnreadCount,

  // Sales summary hook
  useSalesSummary,

  // Expenses hook
  useExpenses,

  // Platform settings hook (global admin settings)
  usePlatformSettings,

  // Cache invalidation (legacy - prefer batchInvalidate)
  invalidateBookings,
  invalidateExpenses,
  invalidateEarnings,
  invalidateAlerts,
  invalidatePayments,
  invalidateServices,
  invalidateStaff,
  invalidateBusinessHours,
  invalidateStaffHours,
  invalidateCustomers,
  invalidateCustomerOffers,
  invalidateSalesSummary,
  invalidateInbox,
  invalidatePlatformSettings,
  useCacheInvalidation,
} from './hooks';

// Types
export type { SalesSummaryData, BookedRange } from './hooks';

export {
  prefetchBusinessData,
  prefetchScheduleForDate,
  prefetchAdjacentDates,
  prefetchDateRange,
  prefetchOnHover,
  prefetchCustomers,
  prefetchSalesSummary,
  prefetchInventory,
  prefetchBookingModalData,
} from './prefetch';

// Mutation utilities - batched invalidation and optimistic updates
export {
  // Batched invalidation
  batchInvalidate,
  flushInvalidations,

  // Optimistic update utilities
  optimisticUpdate,
  optimisticUpdateWithRevalidate,
  updateCacheWithResult,

  // Booking optimistic updates
  optimisticAddBooking,
  optimisticAddBarberAvailability,
  optimisticRemoveBooking,
  optimisticUpdateBookingStatus,
  optimisticUpdateEarnings,
  optimisticAddTransaction,

  // Customer optimistic updates
  optimisticAddCustomer,
  optimisticUpdateCustomer,
  optimisticRemoveCustomer,

  // Service optimistic updates
  optimisticAddService,
  optimisticUpdateService,
  optimisticRemoveService,

  // Staff optimistic updates
  optimisticAddStaff,
  optimisticUpdateStaff,
  optimisticRemoveStaff,

  // Inventory optimistic updates
  optimisticUpdateInventory,

  // Alert optimistic updates
  optimisticDismissAlert,

  // High-level mutation actions
  afterBookingCreated,
  afterPaymentCompleted,
  afterBookingMarkedPaid,
  afterBookingStatusUpdated,
  afterCustomerCreated,
  afterCustomerUpdated,
  afterServiceCreated,
  afterServiceUpdated,
  afterStaffCreated,
  afterStaffUpdated,
  afterAlertDismissed,
} from './mutations';

export type { InvalidationType } from './mutations';

// Optimistic mutation hooks
export {
  useOptimisticMutation,
  optimisticMutate,
  fireAndForgetMutation,
} from './use-optimistic-mutation';

export type {
  OptimisticMutationOptions,
  UseOptimisticMutationReturn,
} from './use-optimistic-mutation';

// Specialized mutation hooks
export {
  useModalMutation,
} from './use-modal-mutation';

export type {
  UseModalMutationOptions,
  UseModalMutationReturn,
} from './use-modal-mutation';

export {
  useToggleMutation,
} from './use-toggle-mutation';

export type {
  UseToggleMutationOptions,
  UseToggleMutationReturn,
} from './use-toggle-mutation';

export {
  useDeleteMutation,
} from './use-delete-mutation';

export type {
  UseDeleteMutationOptions,
  UseDeleteMutationReturn,
} from './use-delete-mutation';

// Dashboard batch hook
export {
  useDashboard,
  invalidateDashboard,
  updateDashboardCache,
  dashboardKey,
} from './dashboard';

export type { DashboardData } from './dashboard';

// Schedule realtime hook
export {
  useScheduleRealtime,
  useScheduleRealtimeSubscription,
  useHomeRealtime,
} from './schedule-realtime';

